import { Injectable, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  SeuFormValue,
  SeuSearchSelectOption,
  SeuSelectColorOption,
  SeuSelectOption,
} from '@seu-form';
import { getBadgeColorFromKey, getListItemName } from '@seu-helpers';
import { SeuConfigTranslationPipe } from '@seu-pipes';
import { LanguageService, StoreService } from '@seu-services';
import {
  SeuComponent,
  SeuConfigComponentTranslation,
  SeuConfigFileRef,
  SeuConfigFormDefinition,
  SeuConfigFormGroupObjectEntry,
  SeuConfigInput,
  SeuConfigInputItem,
  SeuConfigInputValue,
  SeuConfigItem,
  SeuConfigList,
  SeuConfigRadioGroup,
  SeuConfigToggle,
  SeuConfigTranslationObject,
  SeuConfigTranslationSelectOption,
  SeuInterconnection,
  isSeuConfigComponentTranslation,
} from '@seu-types';
import {
  inputSelectGroupRequiredValidator,
  outputsValidator,
} from '@seu-validators';
import {
  cloneDeep,
  compact,
  first,
  isArray,
  isNil,
  last,
  orderBy,
  uniqBy,
} from 'lodash-es';
import { SeuInputSearchGroupValue } from '../form/types/select-input-group';

@Injectable({
  providedIn: 'root',
})
export class ConfigFormService {
  private readonly configTranslationPipe = inject(SeuConfigTranslationPipe);
  private readonly store = inject(StoreService);
  private readonly languageService = inject(LanguageService);

  /**
   * Topic: Form Definition
   * Returns a Label from the Scheuch Config
   */
  public getFormLabel(type: string, key: string): string {
    return this.configTranslationPipe.transform(`${type}.form.${key}`);
  }

  /**
   * Topic: Form Fields
   * Returns the options for a 'multi-select' Form Field
   * Find the corresponding translation in the Scheuch Config and return the options
   * DE.json[translationKey].options[key]
   */
  public getMultiSelectOptions(
    keys: string[],
    translationKey: string
  ): SeuSelectColorOption<string>[] {
    const translations = this.store.getConfigTranslations(
      this.languageService.getLang()
    );
    if (!translations) {
      return [];
    }
    const multiSelectOptions: SeuSelectColorOption<string>[] = [];
    keys.forEach((key) => {
      const translationObject: SeuConfigTranslationObject = translations[
        translationKey
      ] as SeuConfigTranslationObject;
      const allOptions: SeuConfigTranslationObject = translationObject[
        'options'
      ] as SeuConfigTranslationObject;

      const optionsObject: SeuConfigTranslationObject[] = allOptions[
        key
      ] as SeuConfigTranslationObject[];

      if (optionsObject) {
        for (const prop in optionsObject) {
          if (typeof optionsObject[prop] === 'string') {
            multiSelectOptions.push({
              key: prop,
              label: optionsObject[prop],
              color: getBadgeColorFromKey(prop),
            });
          }
        }
      }
    });

    return multiSelectOptions;
  }

  /**
   * Topic: Form Fields
   * Returns the options for a 'input-search-group' Form Field
   * Find the corresponding translation in the Scheuch Config and return the options
   * DE.json.units[key]
   */
  public getUnitOptions(keys: string[]): SeuSearchSelectOption<string>[] {
    const translations = this.store.getConfigTranslations(
      this.languageService.getLang()
    );
    if (!translations) {
      return [];
    }
    const unitOptions: SeuSearchSelectOption<string>[] = [];
    keys.forEach((key) => {
      const allUnits: SeuConfigTranslationObject = translations[
        'units'
      ] as SeuConfigTranslationObject;
      const options: SeuConfigTranslationSelectOption[] = allUnits[
        key
      ] as SeuConfigTranslationSelectOption[];

      if (options && Array.isArray(options)) {
        options.forEach((option) => {
          unitOptions.push({
            key: option.pythonName,
            label: option.displayName,
            keywords: option.synonyms,
          });
        });
      }
    });
    return this.unitOptionsSorted(unitOptions);
  }

  /**
   * Topic: Form Fields
   * Sorts unit options according to the global unitOrder from config
   * Units not in unitOrder are appended at the end
   * Returns original array if unitOrder is undefined or empty
   */
  public unitOptionsSorted(
    options: SeuSearchSelectOption<string>[]
  ): SeuSearchSelectOption<string>[] {
    const config = this.store.getConfig();
    const unitOrder = config?.unitOrder;

    if (!unitOrder || unitOrder.length === 0) {
      return options;
    }

    // Build Map for O(1) lookup performance
    const unitOrderMap = new Map<string, number>();
    unitOrder.forEach((unit, index) => {
      unitOrderMap.set(unit, index);
    });

    return orderBy(
      options,
      [
        (option: SeuSearchSelectOption<string>) => {
          const index = unitOrderMap.get(option.key);
          // Units not in unitOrder get max number (appended at end)
          return index !== undefined ? index : Number.MAX_SAFE_INTEGER;
        },
      ],
      'asc'
    );
  }

  /**
   * Topic: Form Fields
   * Returns the options for a 'select' Form Field
   * Find the corresponding translation in the Scheuch Config and return the options
   * DE.json[configKey].options[key]
   */
  public getSelectOptions(
    keys: string[],
    configKey: string
  ): SeuSelectOption<string>[] {
    const options: SeuSelectOption<string>[] = [];
    const translations = this.store.getConfigTranslations(
      this.languageService.getLang()
    );
    if (!translations) {
      return options;
    }
    const rawEntry = translations[configKey];
    if (!isSeuConfigComponentTranslation(rawEntry)) {
      return options;
    }
    const configItem: SeuConfigComponentTranslation = rawEntry;
    const allOptions = configItem.options;
    if (!allOptions) {
      return options;
    }

    keys.forEach((key) => {
      const optionsObject: SeuConfigTranslationObject = allOptions[
        key
      ] as SeuConfigTranslationObject;

      for (const [key, value] of Object.entries(optionsObject)) {
        options.push({
          key,
          label: value as string,
        });
      }
    });
    return options;
  }

  /**
   * Topic: Form Fields
   * Returns the options for a 'search-select' Form Field
   * Find the corresponding translation in the Scheuch Config and return the options
   * DE.json[key]
   */
  public getSearchSelectOptions(
    data: string | string[]
  ): SeuSearchSelectOption<string>[] {
    const translations =
      this.store.getConfigTranslations(this.languageService.getLang()) ?? {};
    if (!translations) {
      return [];
    }

    const dataArray = isArray(data) ? data : [data];
    let allOptions: SeuConfigTranslationSelectOption[] = [];

    dataArray.forEach((data) => {
      allOptions = allOptions.concat(
        translations[data] as SeuConfigTranslationSelectOption[]
      );
    });

    return compact(
      uniqBy(allOptions, (option) => {
        return option.pythonName;
      })
    ).map((option) => {
      return {
        key: option.pythonName,
        label: option.displayName,
        keywords: option.synonyms,
      };
    });
  }

  /**
   * Topic: FormGroup
   * Used inside offer-form.service.ts
   * to create a FormGroup and prefill it based on the Form Definition
   */
  public initFormGroupFromConfig(
    formKey: string,
    inputs: SeuConfigInputItem[] = []
  ): FormGroup<{
    [key: string]: FormControl | FormArray;
  }> {
    const formGroup = new FormGroup<{
      [key: string]: FormControl | FormArray;
    }>({});

    const formDefinition = this.store.getConfig().formDefinitions[formKey];

    if (!formDefinition) {
      console.error(`Form definition for key ${formKey} not found`);
      return formGroup;
    }

    formDefinition.items.forEach((item) => {
      if ('configFile' in item) {
        return;
      } // skip file references
      const controlItems = this.getConfigControlItems(
        item,
        item.name ?? '',
        inputs,
        formKey
      );

      controlItems.forEach((item) => {
        if (item && item.value) {
          formGroup.addControl(item.key, item.value);
        }
      });
    });

    return formGroup;
  }

  public initSplitterFormGroup(
    component: SeuComponent,
    interconnection: SeuInterconnection
  ): FormGroup {
    const formGroup = new FormGroup({});

    interconnection.operatingConditions.forEach((opc, index) => {
      // inside of the Splitter Form create a FormGroup for every operating condition
      const opcFormGroup = new FormGroup({}, [outputsValidator]);
      const outputsFormArray = new FormArray<FormGroup>([]);

      let distributionTypeValue = 'percent';

      component.outputHandles?.forEach((handle) => {
        // inside of the operating condition FormGroup create a FormArray of output handles which holds the values for this operating condition
        const outputFormGroup = new FormGroup({});

        const valueObject = handle.values?.find(
          (v) => v.operatingConditionId === opc.id
        );

        const isRemainder = valueObject?.isRemainder ?? false;
        const value = valueObject?.isRemainder
          ? null
          : (valueObject?.value ?? null);

        const valueControl = new FormControl(value);
        if (isRemainder) {
          valueControl.disable({ emitEvent: false });
        }
        outputFormGroup.addControl(`value`, valueControl);
        outputFormGroup.addControl(`isRemainder`, new FormControl(isRemainder));
        // add id control (not rendered, but needed to keep track of existing outputs for proper update)
        outputFormGroup.addControl(`handleId`, new FormControl(handle.id));
        outputsFormArray.push(outputFormGroup);

        if (valueObject?.unit) {
          distributionTypeValue = valueObject.unit;
        }
      });

      opcFormGroup.addControl('outputs', outputsFormArray);
      opcFormGroup.addControl(
        'distributionType',
        new FormControl(distributionTypeValue)
      );

      formGroup.addControl(opc.id, opcFormGroup);
    });

    return formGroup;
  }

  /**
   * Topic: FormGroup
   * Used inside initFormGroupFromConfig()
   * to create a FormControl based on the Form Definition
   * is called recursively for nested RADIO_GROUP items or LIST items
   */
  public getConfigControlItems(
    item: SeuConfigItem,
    key: string,
    inputs: SeuConfigInputItem[] = [],
    translationKey: string
  ): SeuConfigFormGroupObjectEntry[] {
    const controlItems: SeuConfigFormGroupObjectEntry[] = [];
    /**
     * INPUT
     * creates a FormControl
     * adds validators based on the Scheuch Config
     * adds a saved or default value if available
     */
    if (item.type === 'INPUT') {
      const savedValue = inputs.find((input) => input.name === key);
      const validators = [];
      if (item.importance !== 'optional') {
        validators.push(Validators.required);
      }
      if (item.units) {
        validators.push(inputSelectGroupRequiredValidator);
      }

      const inputValue = this.getFormValueFromInputItem(savedValue);

      controlItems.push({
        key,
        value: new FormControl(inputValue, validators),
      });
    }

    /**
     * ACCORDION
     * calls this function recursively for all accordion items
     */
    if (item.type === 'ACCORDION') {
      // Add Formcontrol for the accordion name to be able to edit it
      if (item.nameEditable) {
        const savedValue = inputs.find((input) => input.name === key);
        const inputValue = this.getFormValueFromInputItem(savedValue);
        controlItems.push({
          key,
          value: new FormControl(
            inputValue ?? this.getFormLabel(translationKey ?? '', item.name),
            []
          ),
        });
      }

      item.accordionItems.forEach((accordionItem) => {
        // FormControl for the accordionItem
        const controls = this.getConfigControlItems(
          accordionItem,
          accordionItem.name ?? '',
          inputs,
          translationKey
        );
        controls.forEach((_item) => {
          if (_item && _item.value) {
            controlItems.push(_item);
          }
        });
      });
    }

    /**
     * LIST
     * calls this function recursively for all listItems (to init INPUT items)
     */
    if (item.type === 'LIST') {
      // REFACTOR LIST: Add FormArray that holds the listItems
      const formArray = new FormArray<FormGroup | FormArray>([]);

      const activeSavedListItemsLength = inputs.filter((input) => {
        return input.name === first(item.listItems)?.name;
      }).length;

      for (let i = 0; i < activeSavedListItemsLength; i++) {
        const formGroup = new FormGroup({});

        // map the inputs into a new inputs array with updated inputId and filter out the null values afterwards
        const filteredInputs = inputs
          .map((input) => {
            // return null if the input is not a listItem
            if (!input.inputId?.includes('__')) {
              return null;
            }
            // split the listItem's inputId into its indexes and slice the name from the front
            const inputIndexArray = input.inputId.split('__').slice(1);

            // return null if the outermost index is not the same as the current listIndex
            if (Number(last(inputIndexArray)) !== i + 1) {
              return null;
            }

            // now there should only be Items left that belong to the current LIST
            // remove the current listIndex from the inputId to make it work for the next nested LIST
            const newIndexArray = inputIndexArray.slice(0, -1);
            let newInputId = '';
            if (newIndexArray.length) {
              newInputId = `${input.name}__${newIndexArray.join('__')}`;
            } else {
              newInputId = input.name;
            }

            return { ...input, inputId: newInputId };
          })
          .filter((input) => input !== null) as SeuConfigInputItem[];

        item.listItems.forEach((listItem) => {
          // get the nested FormControls for the listItems
          const listControlItems = this.getConfigControlItems(
            listItem,
            listItem.name ?? '',
            filteredInputs,
            translationKey
          );
          listControlItems.forEach((item) => {
            if (item && item.value) {
              formGroup.addControl(item.key, item.value);
            }
          });
        });
        if (formGroup) {
          formArray.push(formGroup);
        }
      }

      // if there are too few listItems, add default items
      for (let i = activeSavedListItemsLength; i < item.initItemCount; i++) {
        const formGroup = new FormGroup({});
        item.listItems.forEach((listItem) => {
          const listControlItems = this.getConfigControlItems(
            listItem,
            listItem.name ?? '',
            [],
            translationKey
          );
          listControlItems.forEach((item) => {
            if (item && item.value) {
              formGroup.addControl(item.key, item.value);
            }
          });
        });
        if (formGroup) {
          formArray.push(formGroup);
        }
      }

      controlItems.push({
        key,
        value: formArray,
      });
    }
    /**
     * RADIO_GROUP
     * creates a FormControl for every Radiobutton Group
     * initialized with the name of the checked radioItem
     * calls this function recursively for all nested items
     */
    if (item.type === 'RADIO_GROUP') {
      item.radioItems.forEach((radioItem) => {
        // get the saved value for the radioItem
        const savedValue = inputs.find((input) => input.name === radioItem.name)
          ?.unitValue.value;

        const isRadioChecked = isNil(savedValue)
          ? (radioItem.checked ?? false)
          : savedValue;

        if (isRadioChecked) {
          // FormControl which holds the name of the checked radioItem
          controlItems.push({
            key,
            value: new FormControl(radioItem.name, []),
          });
        }

        // FormControl for the radioItem
        controlItems.push({
          key: radioItem.name,
          value: new FormControl(isRadioChecked === true ? true : false, []),
          type: 'RADIO_ITEM',
        });

        // get all the FormControls for the nested items
        radioItem.items?.forEach((item) => {
          const controls = this.getConfigControlItems(
            item,
            item.name ?? '',
            inputs,
            translationKey
          );
          controls.forEach((item) => {
            if (item && item.value) {
              controlItems.push(item);
            }
          });
        });
      });
    }

    /**
     * TOGGLE
     * creates a boolean FormControl for the toggle itself
     * calls this function recursively for all nested toggle items
     */
    if (item.type === 'TOGGLE') {
      const savedValue = inputs.find((input) => input.name === key);
      const toggleValue = savedValue
        ? (savedValue.unitValue.value as boolean)
        : (item.toggled ?? false);

      controlItems.push({
        key,
        value: new FormControl<boolean>(toggleValue, []),
      });

      item.toggleItems?.forEach((toggleItem) => {
        const controls = this.getConfigControlItems(
          toggleItem,
          toggleItem.name ?? '',
          inputs,
          translationKey
        );
        controls.forEach((_item) => {
          if (_item && _item.value) {
            controlItems.push(_item);
          }
        });
      });
    }

    return controlItems;
  }

  /**
   * Topic: Form Definition
   * Initializes the Form Definition based on the Scheuch Config
   */
  public initFormDefinitionFromConfig(
    formKey: string,
    form: FormGroup
  ): SeuConfigFormDefinition {
    const formDefinition = this.store.getConfig().formDefinitions[formKey];

    if (!formDefinition) {
      throw new Error(`Form definition for key ${formKey} not found`);
    }

    return {
      ...formDefinition,
      items: formDefinition.items.map(
        (item): SeuConfigItem | SeuConfigFileRef => {
          if ('configFile' in item) {
            return item;
          }
          return this.initConfigItem(form, item, formKey);
        }
      ),
    };
  }

  /**
   * Topic: Form Definition
   * Used inside initFormDefinitionFromConfig()
   * to create a definition object for one config item
   * is called recursively for nested RADIO_GROUP items or LIST items
   */
  private initConfigItem(
    form: FormGroup,
    item: SeuConfigItem,
    translationKey: string
  ): SeuConfigItem {
    /**
     * INPUT
     * adds a key based on the name
     */
    if (item.type === 'INPUT') {
      const configItem: SeuConfigInput = item;
      return configItem;
    }

    /**
     * ACCORDION
     * recursively calls this function for all accordion items
     */
    if (item.type === 'ACCORDION') {
      const configItem = item;
      configItem.accordionItems = item.accordionItems.map((accordionItem) => {
        return this.initConfigItem(form, accordionItem, translationKey);
      });

      return configItem;
    }

    /**
     * LIST
     * adds form definition inside the LIST representing the maximum number of list items
     * calls this function recursively for all list items (to init INPUT items)
     */
    if (item.type === 'LIST') {
      const configItem: SeuConfigList = {
        ...item,
      };

      // also transform the original listItems
      // needed for deleting and duplicating list items
      configItem.listItems = item.listItems.map((listItem) => {
        const configItem = cloneDeep(
          this.initConfigItem(form, listItem, translationKey)
        );
        return configItem;
      });

      return {
        ...configItem,
        label: item.hideLabel
          ? undefined
          : this.getFormLabel(translationKey, item.name),
        tooltip: item.tooltip
          ? this.getFormLabel(translationKey, item.tooltip)
          : undefined,
      };
    }

    /**
     * RADIO_GROUP
     * adds the radioLabel
     * and calls this function recursively for all radioItems (to init INPUT items)
     */
    if (item.type === 'RADIO_GROUP') {
      const configItem: SeuConfigRadioGroup = {
        ...item,
        radioItems: [],
        radioLabel: item.hideLabel
          ? undefined
          : this.getFormLabel(translationKey, item.name),
        tooltip: item.tooltip
          ? this.getFormLabel(translationKey, item.tooltip)
          : undefined,
      };
      item.radioItems.forEach((radioItem) => {
        const configItems: SeuConfigItem[] = [];
        if (radioItem.items?.length) {
          radioItem.items.forEach((item) => {
            configItems.push(
              cloneDeep(this.initConfigItem(form, item, translationKey))
            );
          });
          configItem.radioItems.push({
            name: radioItem.name,
            radioLabel: this.getFormLabel(translationKey, radioItem.name),
            items: configItems,
            checked: radioItem.checked ?? false,
            disabled: radioItem.disabled,
            tooltip: radioItem.tooltip
              ? this.getFormLabel(translationKey, radioItem.tooltip)
              : undefined,
          });
        } else {
          configItem.radioItems.push({
            name: radioItem.name,
            radioLabel: this.getFormLabel(translationKey, radioItem.name),
            checked: radioItem.checked ?? false,
            disabled: radioItem.disabled,
            tooltip: radioItem.tooltip
              ? this.getFormLabel(translationKey, radioItem.tooltip)
              : undefined,
          });
        }
      });

      return configItem;
    }

    /**
     * TOGGLE
     * recursively initializes all nested toggle items
     */
    if (item.type === 'TOGGLE') {
      const configItem: SeuConfigToggle = {
        ...item,
        toggleItems: item.toggleItems?.map((toggleItem) =>
          cloneDeep(this.initConfigItem(form, toggleItem, translationKey))
        ),
      };
      return configItem;
    }

    /**
     * TITLE, SUBTITLE
     * translates the label
     */
    if (item.type === 'TITLE' || item.type === 'SUBTITLE') {
      return {
        ...item,
        name: this.getFormLabel(translationKey, item.name),
        tooltip: item.tooltip
          ? this.getFormLabel(translationKey, item.tooltip)
          : undefined,
      };
    }

    return {
      ...item,
      name: this.getFormLabel(translationKey, item.name ?? ''),
    };
  }

  /**
   * Topic: Saved Values
   * Returns the values of a FormGroup as SeuConfigInputItems
   * Used inside filter-components-detail.component.ts, global-parameters-detail.component.ts, operating-conditions-detail.component.ts to save the form values
   * it's a flat representation of all FormGroup values to be stored in the BE
   */
  public getConfigInputItemsFromFormValues(
    formValues: {
      [key: string]: unknown;
    },
    componentType?: string
  ): SeuConfigInputItem[] {
    // Get the controlItems including a type to be able to identify RADIO_ITEM for the changelog to exclude them
    const values: SeuConfigInputItem[] = [];
    const configItem = this.getFormDefinitionByKey(componentType ?? '');
    const controlItems: SeuConfigFormGroupObjectEntry[] = [];
    configItem?.items.forEach((item) => {
      if ('configFile' in item) {
        return;
      }
      controlItems.push(
        ...this.getConfigControlItems(
          item,
          item.name ?? '',
          [],
          componentType ?? ''
        )
      );
    });

    // iterate over all form values and create a flat representation of the values
    Object.entries(formValues).forEach(([key, formValue]) => {
      const controlItem = controlItems.find(
        (controlItem) => controlItem.key === key
      );
      // FormValue is Array -> MULTI-SELECT or LIST
      if (typeof formValue === 'object' && Array.isArray(formValue)) {
        // MULTI-SELECT: item is of type string or number
        const referenceItem = first(formValue);
        if (
          typeof referenceItem === 'string' ||
          typeof referenceItem === 'number' ||
          formValue.length === 0
        ) {
          values.push({
            inputId: key,
            name: key,
            unitValue: { value: formValue },
            valueType: 'multi',
          });
          return;
        } else if (typeof referenceItem === 'object') {
          // LIST: items are of type object
          formValue.forEach((item, index) => {
            const inputItems = this.getConfigInputItemsFromFormValues(item);

            // modify InputItems with the listIndex
            inputItems.forEach((inputItem) => {
              inputItem.inputId = getListItemName(inputItem.inputId, index + 1);
            });
            values.push(...inputItems);
          });
          return;
        }
      }

      if (typeof formValue === 'number' && !isNaN(formValue)) {
        values.push({
          inputId: key,
          name: key,
          unitValue: { value: formValue },
          valueType: 'number',
        });
        return;
      }

      if (typeof formValue === 'string') {
        values.push({
          inputId: key,
          name: key,
          unitValue: { value: formValue },
          valueType: 'string',
        });
        return;
      }

      if (typeof formValue === 'boolean') {
        values.push({
          inputId: key,
          name: key,
          excludeFromChangelog: controlItem?.type === 'RADIO_ITEM',
          unitValue: { value: formValue },
          valueType: 'bool',
        });
        return;
      }

      if (typeof formValue === 'object') {
        const _formValue = formValue as SeuInputSearchGroupValue<string> | null;

        if (isNil(_formValue?.value) || isNil(_formValue?.option)) {
          // if the value or option is not set, return
          return;
        }

        if (
          typeof _formValue.value !== 'string' &&
          typeof _formValue.value !== 'number'
        ) {
          // if the value is not a string or number return
          // should not happen, but type allows it so we need to check
          return;
        }

        const unitValue = {
          value: _formValue.value,
          unit: _formValue.option,
        };

        values.push({
          inputId: key,
          name: key,
          unitValue,
          valueType: typeof _formValue.value === 'number' ? 'number' : 'string',
        });
        return;
      }
    });
    return values;
  }

  private getFormValueFromInputItem(
    inputItem?: SeuConfigInputItem
  ): SeuConfigInputValue | SeuInputSearchGroupValue<SeuFormValue> | null {
    if (!inputItem) {
      return null;
    }
    if (inputItem.unitValue.unit || inputItem.unitValue.unit === '') {
      return {
        value: inputItem.unitValue.value,
        option: inputItem.unitValue.unit ?? '',
      } as SeuInputSearchGroupValue<SeuFormValue>;
    }
    return inputItem.unitValue.value;
  }

  public getFormDefinitionByKey(key: string): SeuConfigFormDefinition | null {
    const config = this.store.getConfig();
    return config.formDefinitions[key] ?? null;
  }
}
